import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, KeyRound } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signIn } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    // If user is already logged in, redirect to appropriate page
    if (user) {
      const from = location.state?.from?.pathname;
      if (from) {
        navigate(from);
      } else {
        navigate('/profile');
      }
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      await signIn(formData.email, formData.password);
      
      // Check if user has temporary password
      if (sessionStorage.getItem('hasTemporaryPassword') === 'true') {
        toast.warning('You have a temporary password. Please change it now.');
        navigate('/change-temporary-password');
        return;
      }
      
      toast.success('Login successful!');
      // Navigation will be handled by the useEffect above
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message || 'Failed to login. Please check your credentials.');
      } else {
        toast.error('Failed to login. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-zinc-900 rounded-lg p-8">
          <h1 className="text-3xl font-bold text-white mb-8 text-center">
            Welcome Back
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                Email Address
              </label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  required
                  className="mt-1 block w-full rounded-md bg-zinc-800 border-zinc-700 text-white px-4 py-2 pl-10 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm text-primary-500 hover:text-primary-400">
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                id="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                showStrengthMeter={false}
                label=""
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>

            <p className="text-center text-gray-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-500 hover:text-primary-400">
                Register here
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}